import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ListItemIcon, Menu, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  MoreVertRounded as MoreIcon,
  PaymentsOutlined as RepayIcon,
} from '@mui/icons-material';
import api from '../../../../utils/functions/api';
import { confirmAsync } from '../../../../utils/functions/globalDialog';
import { formatCurrency } from '../../utils/format';
import DebtRepaymentDialog from '../dialogs/DebtRepaymentDialog';
import DebtNoteDetailDialog, { getRepaid, getRemaining, sumRemaining } from '../dialogs/DebtNoteDetailDialog';

const EMPTY_FORM = { party: '', amount: '', note: '' };
const NUM_SX = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const HEAD_SX = { py: 1, fontSize: '0.8rem', fontWeight: 600, color: '#64748b', bgcolor: '#f8fafc', borderColor: '#e2e8f0' };
const CELL_SX = { py: 1.25, fontSize: '0.9rem', borderColor: '#f1f5f9' };

// แถบความคืบหน้าการคืนเงินใต้ยอดคงเหลือ (สีเขียว = ส่วนที่คืนแล้ว)
function RepaidBar({ amount, repaid }) {
  if (!amount) return null;
  const pct = Math.min(100, Math.max(0, (repaid / amount) * 100));
  return (
    <Box sx={{ mt: 0.5, ml: 'auto', width: 96, height: 4, borderRadius: 2, bgcolor: '#fee2e2', overflow: 'hidden' }}>
      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#16a34a' }} />
    </Box>
  );
}

// การ์ดเจ้าหนี้/ลูกหนี้ในแท็บ "เจ้าหนี้ / ลูกหนี้" ของหน้า /accounting/loan-pairs — บันทึกจดไว้เฉยๆ (เหมือน
// note) ไม่ผูกกับ Customer/Payment/คู่ยืม-คืน หรือข้อมูลอื่น (backend: modules/debtNote) — category = CREDITOR/DEBTOR
// กดที่แถวเพื่อเปิดรายละเอียด + ประวัติการคืน (DebtNoteDetailDialog), แก้ไข/ลบ อยู่ในเมนู ⋮ ท้ายแถว
export default function DebtNoteList({ category, title, hint, partyLabel, loanBalanceRow = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = ปิด, {} = เพิ่มใหม่, row = แก้ไข
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [repayRow, setRepayRow] = useState(null); // รายการที่กำลังบันทึกการคืน
  const [detailId, setDetailId] = useState(null); // รายการที่เปิดหน้าต่างรายละเอียด
  const [menu, setMenu] = useState(null); // { anchorEl, row } ของเมนู ⋮

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/debt-note?category=${category}`);
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    reload();
  }, [reload]);

  // รายการที่ยังค้างขึ้นก่อน รายการที่คืนครบแล้วลงไปท้ายตาราง (ภายในกลุ่มคงลำดับล่าสุดก่อนจาก backend)
  const sortedRows = useMemo(() => {
    const isSettled = (r) => r.amount != null && getRemaining(r) <= 0;
    return [...rows].sort((a, b) => Number(isSettled(a)) - Number(isSettled(b)));
  }, [rows]);

  const openForm = (row) => {
    setEditing(row || {});
    setForm(row
      ? { party: row.party || '', amount: row.amount ?? '', note: row.note || '' }
      : EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.party.trim()) return;
    setSaving(true);
    try {
      const payload = {
        category,
        party: form.party,
        amount: form.amount === '' ? null : Number(form.amount),
        note: form.note,
      };
      if (editing?._id) {
        await api.put(`${process.env.REACT_APP_API_URL}/debt-note/${editing._id}`, payload);
      } else {
        await api.post(`${process.env.REACT_APP_API_URL}/debt-note`, payload);
      }
      setEditing(null);
      reload();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const ok = await confirmAsync(`ลบรายการ "${row.party}" รวมประวัติการคืนทั้งหมด?`);
    if (!ok) return;
    await api.delete(`${process.env.REACT_APP_API_URL}/debt-note/${row._id}`);
    reload();
  };

  // รายการพิเศษ "ยอดคงเหลือ (ตามจริง)" ของคู่ยืม-คืน — แสดงเมื่อยอดไม่เท่ากับ 0 ไม่ใช่ debt note จริง
  // (ไม่อยู่ใน rows) จึงไม่มีปุ่มคืน/แก้ไข/ลบ แต่ยอดรวมเข้า "คงเหลือรวม" / "จากยอดหนี้" / จำนวนค้างด้วย
  const loanBalanceAmount = Number(loanBalanceRow?.amount) || 0;
  const showLoanBalanceRow = loanBalanceAmount !== 0;

  const total = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0) + loanBalanceAmount;
  const totalRemaining = sumRemaining(rows) + loanBalanceAmount;
  const openCount = rows.filter((r) => r.amount == null || getRemaining(r) > 0).length
    + (loanBalanceAmount > 0 ? 1 : 0);
  const itemCount = rows.length + (showLoanBalanceRow ? 1 : 0);

  // ปุ่มในแถว — กันคลิกทะลุไปเปิดรายละเอียดของแถว
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn(e);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0', overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }} noWrap>
            {hint} · ค้าง {openCount} จาก {itemCount} รายการ
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.3 }}>คงเหลือรวม</Typography>
          <Typography sx={{ ...NUM_SX, fontSize: '1.2rem', fontWeight: 700, color: totalRemaining > 0 ? '#dc2626' : '#16a34a', lineHeight: 1.3 }}>
            {formatCurrency(totalRemaining)}
          </Typography>
          <Typography sx={{ ...NUM_SX, fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.3 }}>
            จากยอดหนี้ {formatCurrency(total)}
          </Typography>
        </Box>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={() => openForm(null)}
          sx={{ flexShrink: 0 }}
        >
          เพิ่มรายการ
        </Button>
      </Stack>

      <TableContainer sx={{ maxHeight: 'calc(100dvh - 240px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_SX}>{partyLabel}</TableCell>
              <TableCell sx={HEAD_SX} align="right">ยอดหนี้</TableCell>
              <TableCell sx={HEAD_SX} align="right">คงเหลือ</TableCell>
              <TableCell sx={HEAD_SX} />
            </TableRow>
          </TableHead>
          <TableBody>
            {showLoanBalanceRow && (
              <TableRow sx={{ bgcolor: '#eff6ff', '& td': { borderColor: '#dbeafe' } }}>
                <TableCell sx={{ ...CELL_SX, maxWidth: 0, width: '45%' }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e3a8a' }} noWrap title={loanBalanceRow.party}>
                    {loanBalanceRow.party}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }} noWrap>
                    ยอดคงเหลือ (ตามจริง) จากหน้ายืม-คืน
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...CELL_SX, ...NUM_SX, color: '#334155' }} align="right">
                  {formatCurrency(loanBalanceAmount)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, ...NUM_SX }} align="right">
                  <Box sx={{ fontWeight: 700, color: loanBalanceAmount > 0 ? '#dc2626' : '#16a34a' }}>
                    {formatCurrency(loanBalanceAmount)}
                  </Box>
                </TableCell>
                <TableCell sx={CELL_SX} />
              </TableRow>
            )}
            {!loading && rows.length === 0 && !showLoanBalanceRow && (
              <TableRow>
                <TableCell colSpan={4} sx={{ ...CELL_SX, py: 4, textAlign: 'center', color: '#94a3b8' }}>
                  ยังไม่มีรายการ — กด &quot;เพิ่มรายการ&quot; เพื่อเริ่มบันทึก
                </TableCell>
              </TableRow>
            )}
            {sortedRows.map((row) => {
              const repaid = getRepaid(row);
              const remaining = getRemaining(row);
              const settled = remaining != null && remaining <= 0;
              let remainingColor = '#cbd5e1';
              if (remaining != null) remainingColor = remaining > 0 ? '#dc2626' : '#16a34a';
              return (
                <TableRow
                  key={row._id}
                  hover
                  onClick={() => setDetailId(row._id)}
                  sx={{ cursor: 'pointer', opacity: settled ? 0.65 : 1 }}
                >
                  <TableCell sx={{ ...CELL_SX, maxWidth: 0, width: '45%' }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }} noWrap title={row.party}>
                      {row.party}
                    </Typography>
                    {row.note && (
                      <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }} noWrap title={row.note}>
                        {row.note.split('\n')[0]}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, ...NUM_SX, color: '#334155' }} align="right">
                    {row.amount == null ? '–' : formatCurrency(row.amount)}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, ...NUM_SX }} align="right">
                    <Box sx={{ fontWeight: 700, color: remainingColor }}>
                      {settled ? 'คืนครบแล้ว' : remaining == null ? '–' : formatCurrency(remaining)}
                    </Box>
                    {!settled && <RepaidBar amount={row.amount} repaid={repaid} />}
                    {repaid > 0 && !settled && (
                      <Box sx={{ mt: 0.25, fontSize: '0.75rem', color: '#16a34a' }}>คืนแล้ว {formatCurrency(repaid)}</Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, py: 0.5, pr: 1, whiteSpace: 'nowrap' }} align="right">
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      startIcon={<RepayIcon />}
                      onClick={stop(() => setRepayRow(row))}
                      sx={{ mr: 0.5 }}
                    >
                      บันทึกการคืน
                    </Button>
                    <IconButton onClick={stop((e) => setMenu({ anchorEl: e.currentTarget, row }))} aria-label="เพิ่มเติม">
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menu?.anchorEl}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { const { row } = menu; setMenu(null); openForm(row); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          แก้ไข
        </MenuItem>
        <MenuItem onClick={() => { const { row } = menu; setMenu(null); handleDelete(row); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          ลบ
        </MenuItem>
      </Menu>

      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing?._id ? `แก้ไข${title}` : `เพิ่ม${title}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={`ชื่อ${partyLabel}`}
              required
              value={form.party}
              onChange={(e) => setForm((f) => ({ ...f, party: e.target.value }))}
              autoFocus
            />
            <TextField
              label="ยอดหนี้ (บาท)"
              type="number"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            />
            <TextField
              label="หมายเหตุ"
              multiline
              minRows={3}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditing(null)} disabled={saving}>ยกเลิก</Button>
          <Button variant="contained" disableElevation onClick={handleSave} disabled={saving || !form.party.trim()}>
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>

      <DebtRepaymentDialog
        note={repayRow}
        remaining={getRemaining(repayRow)}
        onClose={() => setRepayRow(null)}
        onSaved={() => { setRepayRow(null); reload(); }}
      />
      <DebtNoteDetailDialog
        noteId={detailId}
        title={title}
        onClose={() => setDetailId(null)}
        onChanged={reload}
      />
    </Paper>
  );
}
